// 22 §1.7 + web-artifacts-rows-and-versions: הסוג וגרסת הסוג כתווית נפרדת מגרסת היצירה ("אפליקציה 1", ליוצר עם חדש —
// "אפליקציה V1 → זמינה V2") + "שדרג" (תור מזורע — שיחה חדשה עם אייבי שההודעה הראשונה שלה היא הבקשה). לשונית מפרט (SPEC.md — גם לשותף).
// בנייה וקבצים — לא במסך (מידע של הבונה; אייבי מקבלת אותו ב-artifact.get וב-summaryHe).
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { renderMarkdown } from '@aibify/display/client';
import { aiApi } from '../lib/aiApi.ts';
import { api } from '../lib/api.ts';
import type { ArtifactSummary } from '../lib/contract.ts';
import { T } from '../lib/strings.ts';
import { Button } from './ui/button.tsx';
import { ErrorView } from './Status.tsx';
import { IconSparkles } from './brand/icons.tsx';

/** ליוצר בלבד: גרסת סוג חדשה מזו של היצירה. shared קיים רק ליוצר (09 §1.1). */
export const kindUpgradeAvailable = (a: Pick<ArtifactSummary, 'shared' | 'kindVersion' | 'latestKindVersion'>) => !!a.shared && !!a.kindVersion && !!a.latestKindVersion && a.latestKindVersion > a.kindVersion;
/** "שדרוג זמין" אחד לכל הסיבות (22 §1.7 + 25 §1.5): גרסת סוג חדשה, או רכיב מוכן שהיצירה בנויה עליו התעדכן (upgradeAvailable מה-api). */
export const upgradeAvailable = (a: ArtifactSummary) => !!a.shared && (a.upgradeAvailable === true || kindUpgradeAvailable(a));

/** תור מזורע (15): שיחה חדשה ברמה 2 + ההודעה כהודעת משתמש → /ai2/:id. */
function useSeededTurn() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  /** onCreated — אחרי שהשיחה נוצרה ולפני ההודעה (למשל רישום הקשר ליצירה); כשל בו עוצר הכול. */
  const start = async (text: string, onCreated?: (id: string) => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const { id } = await aiApi.createConversation(2);
      if (onCreated) await onCreated(id);
      await aiApi.sendTurn(id, text, [], crypto.randomUUID());
      nav(`/ai2/${id}`);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  return { start, busy, error };
}

/** תווית הסוג: "אפליקציה V1"; דוח/שיחה על גרסת סוג 1 — שם הסוג בלבד; ליוצר עם גרסת סוג חדשה — "… → זמינה V2". */
export function kindLabel(a: Pick<ArtifactSummary, 'kind' | 'kindVersion' | 'latestKindVersion' | 'shared'>): string {
  const kindHe = T.artifacts.kinds[a.kind] ?? a.kind;
  const up = kindUpgradeAvailable(a);
  const base = T.kinds.label(kindHe, a.kindVersion, a.kind === 'app' || (a.kindVersion ?? 1) > 1 || up);
  return up ? T.kinds.available(base, a.latestKindVersion!) : base;
}

/** "שדרג" (היוצר בלבד; רק כשיש שדרוג — גרסת סוג חדשה או רכיב מוכן שהתעדכן). */
export function UpgradeButton({ a }: { a: ArtifactSummary }) {
  const seeded = useSeededTurn();
  if (!upgradeAvailable(a)) return null;
  return (
    <>
      <Button
        size="sm"
        className="gap-1.5"
        disabled={seeded.busy}
        // גרסת סוג חדשה — "מ-app 1 ל-app 2"; רק רכיב מוכן — בלי שם ובלי גרסה (25 §3: מוסתר; אייבי יודעת מ-upgrades[])
        onClick={() => void seeded.start(kindUpgradeAvailable(a) ? T.kinds.upgradePrompt(a.nameHe, a.kind, a.kindVersion!, a.latestKindVersion!) : T.kinds.upgradePromptPlain(a.nameHe))}
        data-testid="upgrade-btn"
      >
        <IconSparkles size={16} />
        {seeded.busy ? T.kinds.opening : T.kinds.upgrade}
      </Button>
      {seeded.error != null && <ErrorView error={seeded.error} />}
    </>
  );
}

/**
 * artifacts-talk-to-aibi: "דבר עם אייבי" — creator/admin בלבד (המסך מסתיר ממשתמש רגיל). ממשיך את השיחה האחרונה **של הצופה**
 * על היצירה (sys.conversation_ref); אין — שיחה חדשה עם הודעה פותחת שמזהה את היצירה, ונרשמת (action='talk') לפני ההודעה.
 */
export function TalkToAibiButton({ slug, nameHe }: { slug: string; nameHe: string }) {
  const nav = useNavigate();
  const seeded = useSeededTurn();
  const [looking, setLooking] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const busy = looking || seeded.busy;
  const onClick = async () => {
    setLooking(true);
    setError(null);
    try {
      const { conversationId } = await api.aibiConversation(slug);
      if (conversationId) return nav(`/ai2/${conversationId}`);
      setLooking(false);
      await seeded.start(T.kinds.talkPrompt(nameHe, slug), (id) => api.linkAibiConversation(slug, id));
    } catch (e) {
      setError(e);
      setLooking(false);
    }
  };
  const shownError = error ?? seeded.error;
  return (
    <div className="flex shrink-0 flex-col gap-2">
      <Button variant="outline" className="shrink-0 gap-1.5" disabled={busy} onClick={() => void onClick()} data-testid="talk-aibi-btn">
        <IconSparkles size={18} />
        {/* במובייל — אייקון בלבד (הטקסט נשאר לקורא מסך), כדי שהכותרת לא תידחק בפס הפעולות */}
        <span className="max-sm:sr-only">{busy ? T.kinds.opening : T.kinds.talk}</span>
      </Button>
      {shownError != null && <ErrorView error={shownError} />}
    </div>
  );
}

/** לשונית "מפרט": SPEC.md מרונדר (markdown מסונן של display). */
export function SpecTab({ spec }: { spec: string }) {
  const html = useMemo(() => renderMarkdown(spec, { dirAuto: true }), [spec]);
  // מסונן ב-renderMarkdown (marked + DOMPurify, רשימת תגיות סגורה)
  return <div className="ab-md ab-md-chat max-w-3xl rounded-md border border-border bg-card p-4 shadow-card md:p-6" data-testid="spec" dangerouslySetInnerHTML={{ __html: html }} />;
}
